import { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getPolicies } from "../lib/api";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import {
  SquareAsterisk,
  LayoutDashboard,
  Receipt,
  CheckSquare,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  Plus,
  User,
  ChevronRight,
  Download,
  X,
  RefreshCw,
  BookOpen
} from "lucide-react";
import { cn } from "../lib/utils";
import { toast } from "sonner";

const roleLabels = {
  collaborator: "Collaborateur",
  manager: "Manager",
  accountant: "Comptable",
  admin: "Administrateur"
};

export default function Layout() {
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [companyName, setCompanyName] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  useEffect(() => {
    getPolicies().then(res => {
      setCompanyName(res.data.company_name || null);
      setCompanyLogo(res.data.company_logo || null);
    }).catch(() => {});
  }, []);

  // Bannière d'installation PWA
  useEffect(() => {
    const dismissed = localStorage.getItem("pwa-install-dismissed");
    if (dismissed) return;

    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Toast de mise à jour SW
  useEffect(() => {
    const handler = (e) => {
      toast("Mise à jour disponible", {
        description: "Une nouvelle version de NoteFrais est disponible.",
        action: {
          label: "Actualiser",
          onClick: () => {
            if (e.detail && e.detail.waiting) {
              e.detail.waiting.postMessage({ type: "SKIP_WAITING" });
            }
            window.location.reload();
          },
        },
        duration: Infinity,
      });
    };
    window.addEventListener("sw-update", handler);
    return () => window.removeEventListener("sw-update", handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") {
      setShowInstallBanner(false);
      setInstallPrompt(null);
    }
  };

  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
    localStorage.setItem("pwa-install-dismissed", "1");
  };

  const navigation = [
    { name: "Tableau de bord", href: "/", icon: LayoutDashboard, roles: [] },
    { name: "Mes dépenses", href: "/expenses", icon: Receipt, roles: [] },
    { name: "Validations", href: "/approvals", icon: CheckSquare, roles: ["manager", "accountant", "admin"] },
    { name: "Rapports", href: "/reports", icon: BarChart3, roles: ["manager", "accountant", "admin"] },
    { name: "Paramètres", href: "/settings", icon: Settings, roles: ["accountant", "admin"] },
    { name: "Mode d'emploi", href: "/help", icon: BookOpen, roles: [] }
  ];

  const filteredNav = navigation.filter(item => 
    item.roles.length === 0 || item.roles.some(role => hasRole(role))
  );

  const NavLink = ({ item, mobile = false }) => {
    const isActive = location.pathname === item.href;
    return (
      <Link
        to={item.href}
        onClick={() => mobile && setMobileOpen(false)}
        className={cn(
          "nav-link",
          isActive && "active"
        )}
        data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
      >
        <item.icon className="h-5 w-5" />
        {item.name}
      </Link>
    );
  };

  const SidebarContent = ({ mobile = false }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b">
        <Link to="/" className="flex items-center gap-2 text-xl font-bold tracking-tighter" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {companyLogo
            ? <img src={companyLogo} alt="Logo" className="h-7 w-auto max-w-[28px] object-contain" />
            : <SquareAsterisk className="h-7 w-7 text-accent" />
          }
          <span>{companyName || "Expenses.ai"}</span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {filteredNav.map((item) => (
          <NavLink key={item.href} item={item} mobile={mobile} />
        ))}
      </nav>

      {/* Quick Action */}
      <div className="p-4 border-t">
        <Link to="/expenses/new" onClick={() => mobile && setMobileOpen(false)}>
          <Button className="w-full gap-2" data-testid="sidebar-new-expense">
            <Plus className="h-4 w-4" />
            Nouvelle dépense
          </Button>
        </Link>
      </div>

      {/* User */}
      <div className="p-4 border-t">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-primary text-primary-foreground">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-muted-foreground">
              {roleLabels[user?.role] || user?.role}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:fixed md:inset-y-0 md:left-0 md:z-50 md:flex md:w-64 md:flex-col bg-card border-r">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden sticky top-0 z-40 bg-card border-b">
        <div className="flex items-center justify-between p-4">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64">
              <SidebarContent mobile />
            </SheetContent>
          </Sheet>
          
          <Link to="/" className="flex items-center gap-2 text-lg font-bold tracking-tighter" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {companyLogo
              ? <img src={companyLogo} alt="Logo" className="h-6 w-auto max-w-[24px] object-contain" />
              : <SquareAsterisk className="h-6 w-6 text-accent" />
            }
            <span>{companyName || "Expenses.ai"}</span>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="user-menu">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { navigate("/profile"); setMobileOpen(false); }} data-testid="profile-mobile">
                <User className="h-4 w-4 mr-2" />
                Mon profil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { navigate("/help"); setMobileOpen(false); }}>
                <BookOpen className="h-4 w-4 mr-2" />
                Mode d'emploi
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-mobile">
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Desktop Header */}
      <header className="hidden md:block fixed top-0 right-0 left-64 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="flex items-center justify-between px-8 h-16">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Accueil</Link>
            {location.pathname !== "/" && (
              <>
                <ChevronRight className="h-4 w-4" />
                <span className="text-foreground font-medium">
                  {filteredNav.find(n => n.href === location.pathname)?.name || "Détail"}
                </span>
              </>
            )}
          </div>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2" data-testid="user-menu-desktop">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden lg:inline">{user?.first_name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {roleLabels[user?.role] || user?.role}
                </p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="profile-link">
                <User className="h-4 w-4 mr-2" />
                Mon profil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/help")}>
                <BookOpen className="h-4 w-4 mr-2" />
                Mode d'emploi
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout">
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="md:pl-64 pt-0 md:pt-16">
        <div className="p-6 md:p-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile FAB */}
      <Link
        to="/expenses/new"
        className="md:hidden fixed bottom-6 right-6 z-50"
      >
        <Button size="lg" className="h-14 w-14 rounded-full shadow-lg" data-testid="fab-new-expense">
          <Plus className="h-6 w-6" />
        </Button>
      </Link>

      {/* Bannière d'installation PWA */}
      {showInstallBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-between gap-3 bg-card border-t px-4 py-3 shadow-lg md:bottom-4 md:left-4 md:right-auto md:max-w-sm md:rounded-xl md:border">
          <Download className="h-5 w-5 text-emerald-500 shrink-0" />
          <p className="flex-1 text-sm font-medium">Installer l'application</p>
          <Button size="sm" onClick={handleInstall} className="shrink-0">
            Installer
          </Button>
          <button
            onClick={dismissInstallBanner}
            className="text-muted-foreground hover:text-foreground shrink-0"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
